import React, { useEffect, useRef, useState } from 'react';
import { conversationService, templateService, conversationAnnotationService } from '../services/api';
import AnnotatableMessage from './Conversation/AnnotatableMessage';
import AnnotationsPanel from './Conversation/AnnotationsPanel';
import FloatingAnnotationForm from './Conversation/FloatingAnnotationForm';
import { TEST_PLAN_SCOPE } from './ClinicDetail/buildTestPlanCasesFromConversation';

const ConversationDetail = ({
  conversation,
  isLoading,
  onSendToClinic,
  onSendMessage,
  showRefreshButton,
  onRefresh,
  lastRefreshTime,
  onLoadMoreMessages,
  isLoadingMore,
  hasMoreMessages,
  clinicId,
  onRequestAddToTestPlan,
}) => {
  // Create a ref for the messages container
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const [message, setMessage] = useState('');
  // Track whether we should scroll to bottom
  const [shouldScrollToBottom, setShouldScrollToBottom] = useState(true);
  // Track last messages length to determine if new messages arrived
  const lastMessagesLengthRef = useRef(0);
  // Track if messages were loaded from the "Load more" button
  const [loadedViaButton, setLoadedViaButton] = useState(false);

  // State for template dialog
  const [isTemplateDialogOpen, setIsTemplateDialogOpen] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [isLoadingTemplates, setIsLoadingTemplates] = useState(false);

  // State for annotations
  const [isAnnotationsPanelOpen, setIsAnnotationsPanelOpen] = useState(false);
  const [annotations, setAnnotations] = useState([]);
  const [annotatedMessageIds, setAnnotatedMessageIds] = useState(new Set());

  // State for batch annotation mode
  const [isBatchAnnotationMode, setIsBatchAnnotationMode] = useState(false);
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const [selectedMessageIds, setSelectedMessageIds] = useState(new Set());

  // State for floating annotation form
  const [isFloatingFormVisible, setIsFloatingFormVisible] = useState(false);
  const [isAddToTestPlanMenuOpen, setIsAddToTestPlanMenuOpen] = useState(false);
  const addToTestPlanMenuRef = useRef(null);

  // Function to instantly teleport to bottom (no smooth scrolling)
  const teleportToBottom = (forceScroll = false) => {
    if (messagesContainerRef.current && (shouldScrollToBottom || forceScroll) && !loadedViaButton) {
      messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
    }
  };

  // Alternative scroll method using messagesEndRef
  const scrollToBottom = (forceScroll = false) => {
    if (messagesEndRef.current && (shouldScrollToBottom || forceScroll) && !loadedViaButton) {
      messagesEndRef.current.scrollIntoView({ behavior: 'auto' });
    }
  };

  // Initial load scroll to bottom
  useEffect(() => {
    // Scroll when component first mounts
    teleportToBottom(true);
    scrollToBottom(true);
  }, []);

  // Added: Teleport to bottom when messages initially load
  useEffect(() => {
    // This ensures we scroll to bottom when messages first load
    // but not when messages are loaded via the "Load more" button
    if (conversation?.messages && conversation.messages.length > 0 && !loadedViaButton) {
      setTimeout(() => {
        teleportToBottom(true); // Force scroll regardless of shouldScrollToBottom state
        scrollToBottom(true); // Also use the scrollIntoView method
      }, 100); // Small delay to ensure DOM is fully rendered
    }
  }, [conversation?.messages?.length, isLoadingMore, loadedViaButton]);

  // Function to handle sending a message
  const sendMessage = () => {
    if (message.trim() && conversation?.conversation_id && onSendMessage) {
      onSendMessage(conversation.conversation_id, message.trim());
      setMessage(''); // Clear the input field after sending
    }
  };

  // Handle keypress event for the input field
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault(); // Prevent default Enter behavior
      sendMessage();
    }
  };

  // Function to open template dialog and fetch templates
  const openTemplateDialog = async () => {
    setIsTemplateDialogOpen(true);
    setIsLoadingTemplates(true);

    try {
      const response = await templateService.getTemplates();
      setTemplates(response.templates);
    } catch (error) {
      console.error('Error fetching templates:', error);
    } finally {
      setIsLoadingTemplates(false);
    }
  };

  // Function to send selected template
  const sendTemplate = async (templateId) => {
    if (!conversation?.conversation_id) return;

    try {
      await conversationService.sendTemplate(conversation.conversation_id, templateId);
      // After sending, close the dialog and refresh messages if needed
      setIsTemplateDialogOpen(false);
      if (onRefresh) onRefresh();
    } catch (error) {
      console.error('Error sending template:', error);
    }
  };

  // Function to load annotations for the current conversation
  const loadAnnotations = async () => {
    if (!conversation?.conversation_id) return;

    try {
      const data = await conversationAnnotationService.getConversationAnnotations(conversation.conversation_id);
      setAnnotations(data);

      // Create a set of annotated message IDs for highlighting
      const annotatedIds = new Set();
      data.forEach(annotation => {
        annotation.selected_message_ids.forEach(id => annotatedIds.add(id));
      });
      setAnnotatedMessageIds(annotatedIds);
    } catch (error) {
      console.error('Error loading annotations:', error);
    }
  };

  // Function to handle annotation creation
  const handleAnnotationCreated = (newAnnotation) => {
    setAnnotations(prev => [newAnnotation, ...prev]);

    // Update annotated message IDs
    const newAnnotatedIds = new Set(annotatedMessageIds);
    newAnnotation.selected_message_ids.forEach(id => newAnnotatedIds.add(id));
    setAnnotatedMessageIds(newAnnotatedIds);
  };

  // Function to handle annotation deletion
  const handleAnnotationDeleted = (deletedAnnotationId) => {
    const deletedAnnotation = annotations.find(ann => ann.id === deletedAnnotationId);
    if (deletedAnnotation) {
      setAnnotations(prev => prev.filter(ann => ann.id !== deletedAnnotationId));

      // Remove deleted annotation's message IDs from annotated set
      const newAnnotatedIds = new Set(annotatedMessageIds);
      deletedAnnotation.selected_message_ids.forEach(id => newAnnotatedIds.delete(id));
      setAnnotatedMessageIds(newAnnotatedIds);
    }
  };

  // Function to toggle batch annotation mode
  const toggleBatchAnnotationMode = () => {
    setIsBatchAnnotationMode(!isBatchAnnotationMode);
    if (isBatchAnnotationMode) {
      // Clear selections when exiting batch mode
      clearRangeSelection();
    }
  };

  // Function to handle range selection in batch mode
  const handleRangeSelection = (messageId) => {
    if (!rangeStart) {
      // First click - set start of range
      setRangeStart(messageId);
      setRangeEnd(null);
      setSelectedMessageIds(new Set([messageId]));
    } else if (rangeStart === messageId) {
      // Clicking same message - clear selection
      setRangeStart(null);
      setRangeEnd(null);
      setSelectedMessageIds(new Set());
    } else {
      // Second click - set end of range and select all messages in between
      setRangeEnd(messageId);

      // Find the indices of start and end messages
      const startIndex = conversation.messages.findIndex(msg => msg.id === rangeStart);
      const endIndex = conversation.messages.findIndex(msg => msg.id === messageId);

      if (startIndex !== -1 && endIndex !== -1) {
        // Determine the range (start to end, inclusive)
        const minIndex = Math.min(startIndex, endIndex);
        const maxIndex = Math.max(startIndex, endIndex);

        // Select all messages in the range
        const rangeMessageIds = conversation.messages
          .slice(minIndex, maxIndex + 1)
          .map(msg => msg.id);

        setSelectedMessageIds(new Set(rangeMessageIds));
      }
    }
  };

  // Function to clear range selection
  const clearRangeSelection = () => {
    setRangeStart(null);
    setRangeEnd(null);
    setSelectedMessageIds(new Set());
    setIsFloatingFormVisible(false);
  };

  // Function to create batch annotation
  const handleBatchAnnotation = async (annotationData) => {
    try {
      const batchAnnotationData = {
        ...annotationData,
        selected_message_ids: Array.from(selectedMessageIds),
        conversation_id: conversation.conversation_id
      };

      const result = await conversationAnnotationService.createAnnotation(batchAnnotationData);
      console.log('Batch annotation created:', result);

      // Update annotations and annotated message IDs
      setAnnotations(prev => [result, ...prev]);

      const newAnnotatedIds = new Set(annotatedMessageIds);
      result.selected_message_ids.forEach(id => newAnnotatedIds.add(id));
      setAnnotatedMessageIds(newAnnotatedIds);

      // Exit batch mode and clear selections
      setIsBatchAnnotationMode(false);
      clearRangeSelection();
      setIsFloatingFormVisible(false);

    } catch (error) {
      console.error('Error creating batch annotation:', error);
      alert('Failed to create batch annotation. Please try again.');
    }
  };

  // Teleport to bottom when component mounts or conversation changes
  useEffect(() => {
    // Only scroll to bottom for new conversation if not loaded via button
    if (!loadedViaButton) {
      setShouldScrollToBottom(true);
      teleportToBottom();
    }

    // Reset last messages length
    if (conversation?.messages) {
      lastMessagesLengthRef.current = conversation.messages.length;
    }
  }, [conversation?.conversation_id, loadedViaButton]);

  // Load annotations when conversation changes
  useEffect(() => {
    if (conversation?.conversation_id) {
      loadAnnotations();
    }
  }, [conversation?.conversation_id]);

  useEffect(() => {
    if (!isAddToTestPlanMenuOpen) return undefined;
    const handleClickOutside = (event) => {
      if (addToTestPlanMenuRef.current && !addToTestPlanMenuRef.current.contains(event.target)) {
        setIsAddToTestPlanMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isAddToTestPlanMenuOpen]);

  // Teleport to bottom only when new messages arrive
  useEffect(() => {
    if (conversation?.messages) {
      // Only scroll if new messages have been added AND not loaded via button
      if (conversation.messages.length > lastMessagesLengthRef.current && !loadedViaButton) {
        teleportToBottom();
      }

      // Update last messages length
      lastMessagesLengthRef.current = conversation.messages.length;
    }
  }, [conversation?.messages, loadedViaButton]);

  // Function to format message content by replacing newlines with <br/> tags
  const formatMessageContent = (content) => {
    if (!content) return '';

    // Replace all occurrences of \n or \r\n with <br/>
    const formattedContent = content
      .replace(/\r\n/g, '<br/>')
      .replace(/\n/g, '<br/>')
      .replace(/\r/g, '<br/>');

    return (
      <span dangerouslySetInnerHTML={{ __html: formattedContent }} />
    );
  };

  if (!conversation) {
    return (
      <div className="h-[70vh] w-full flex items-center justify-center">
        <p className="text-gray-500">Select a conversation to view details</p>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex flex-col h-[70vh] w-full bg-white rounded-lg overflow-hidden shadow-md">
        {/* WhatsApp-like header */}
        <div className="bg-[#075e54] text-white p-3 flex items-center">
          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
            {conversation.profile_name ? conversation.profile_name.charAt(0).toUpperCase() : '#'}
          </div>
          <div>
            <h3 className="font-semibold">
              {conversation.profile_name || conversation.conversation_id}
            </h3>
            <p className="text-xs opacity-80">
              {conversation.channel || 'Unknown channel'}
            </p>
          </div>
        </div>

        {/* Loading indicator */}
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="w-12 h-12 border-4 border-[#075e54] border-t-transparent rounded-full animate-spin mx-auto"></div>
            <p className="mt-4 text-gray-500">Loading messages...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!conversation.messages || conversation.messages.length === 0) {
    return (
      <div className="flex flex-col h-[70vh] w-full bg-white rounded-lg overflow-hidden shadow-md">
        {/* WhatsApp-like header */}
        <div className="bg-[#075e54] text-white p-3 flex items-center">
          <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
            {conversation.profile_name ? conversation.profile_name.charAt(0).toUpperCase() : '#'}
          </div>
          <div>
            <h3 className="font-semibold">
              {conversation.profile_name || conversation.conversation_id}
            </h3>
            <p className="text-xs opacity-80">
              {conversation.channel || 'Unknown channel'}
            </p>
          </div>
        </div>

        {/* No messages indicator */}
        <div className="flex-1 flex items-center justify-center">
          <p className="text-gray-500">No messages in this conversation</p>
        </div>
      </div>
    );
  }

  // Determine message direction based on from_number and to_number
  const getMessageDirection = (message) => {
    if (!message) return 'outbound';

    // If the message has a direction property, use it
    if (message.direction) return message.direction;

    // Otherwise, determine direction based on role or from_number
    if (message.role === 'assistant' || message.role === 'system' || message.role === 'mctb') {
      return 'outbound';
    } else if (message.role === 'user') {
      return 'inbound';
    }

    // Fallback to checking from_number (this depends on your specific data structure)
    // You might need to adjust this logic based on your actual data
    return message.from_number === conversation.to_number ? 'outbound' : 'inbound';
  };

  return (
    <div className="flex flex-col h-[70vh] w-full bg-white rounded-lg overflow-hidden shadow-md">
      {/* WhatsApp-like header with test case controls */}
      <div className={`p-3 transition-all duration-300 ${isBatchAnnotationMode
          ? 'bg-gradient-to-r from-orange-600 to-yellow-600'
          : 'bg-[#075e54]'
        } text-white`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center mr-3">
              {conversation.profile_name ? conversation.profile_name.charAt(0).toUpperCase() : '#'}
            </div>
            <div>
              <h3 className="font-semibold">
                {conversation.profile_name || conversation.conversation_id}
                {isBatchAnnotationMode && (
                  <span className="ml-2 text-sm bg-white text-orange-600 px-2 py-1 rounded-full">
                    Annotate
                  </span>
                )}
              </h3>
              <p className="text-xs opacity-80">
                {conversation.channel || 'Unknown channel'}
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={toggleBatchAnnotationMode}
              className={`px-3 py-1 rounded text-sm font-medium flex items-center transition-all duration-200 ${isBatchAnnotationMode
                  ? 'bg-orange-600 hover:bg-orange-700 text-white'
                  : 'bg-green-600 hover:bg-green-700 text-white'
                }`}
              title={isBatchAnnotationMode ? 'Exit Annotation Mode' : 'Enter Annotation Mode'}
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
              </svg>
              <span>
                {isBatchAnnotationMode
                  ? `Cancel (${selectedMessageIds.size} selected)`
                  : 'Annotate'
                }
              </span>
            </button>

            {/* Confirm Selection Button - only show when messages are selected in range mode */}
            {isBatchAnnotationMode && selectedMessageIds.size > 0 && (
              <button
                onClick={() => setIsFloatingFormVisible(true)}
                className="px-3 py-1 rounded text-sm font-medium flex items-center bg-blue-600 hover:bg-blue-700 text-white transition-all duration-200 transform hover:scale-105 active:scale-95"
                title={`Confirm selection of ${selectedMessageIds.size} message${selectedMessageIds.size > 1 ? 's' : ''}`}
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                <span>Confirm Selection ({selectedMessageIds.size})</span>
              </button>
            )}
            <button
              onClick={() => setIsAnnotationsPanelOpen(true)}
              className="px-3 py-1 rounded text-sm font-medium flex items-center bg-purple-600 hover:bg-purple-700 text-white"
              title="View All Annotations"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>All Annotations ({annotations.length})</span>
            </button>
            <button
              onClick={() => window.exportTestCases && window.exportTestCases()}
              className="px-3 py-1 rounded text-sm font-medium flex items-center bg-indigo-600 hover:bg-indigo-700 text-white"
              title="Export Test Cases"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
              </svg>
              <span>Export test case</span>
            </button>
            {onRequestAddToTestPlan && (
              <div className="relative" ref={addToTestPlanMenuRef}>
                <button
                  type="button"
                  onClick={() => setIsAddToTestPlanMenuOpen((open) => !open)}
                  className="px-3 py-1 rounded text-sm font-medium flex items-center bg-teal-600 hover:bg-teal-700 text-white"
                  title="Add conversation to a test plan"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                  </svg>
                  <span>Add to test plan</span>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 ml-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                  </svg>
                </button>
                {isAddToTestPlanMenuOpen && (
                  <div className="absolute right-0 mt-1 w-56 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-20 py-1">
                    <button
                      type="button"
                      disabled={annotations.length === 0}
                      onClick={() => {
                        setIsAddToTestPlanMenuOpen(false);
                        onRequestAddToTestPlan({ scope: TEST_PLAN_SCOPE.ALL_ANNOTATIONS });
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      All annotations ({annotations.length})
                    </button>
                    <button
                      type="button"
                      onClick={() => {
                        setIsAddToTestPlanMenuOpen(false);
                        onRequestAddToTestPlan({ scope: TEST_PLAN_SCOPE.FULL_CONVERSATION });
                      }}
                      className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                    >
                      Full conversation
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* Range selection instructions */}
        {isBatchAnnotationMode && (
          <div className="pt-2">
            <p className="text-xs text-orange-100 opacity-90">
              {!rangeStart
                ? '💡 Click on a message to start range selection'
                : !rangeEnd
                  ? '💡 Click on another message to complete range selection'
                  : `✅ Range selected: ${selectedMessageIds.size} messages`
              }
            </p>
          </div>
        )}
      </div>

      {/* Chat area with white background and scroll handler */}
      <div
        ref={messagesContainerRef}
        className="flex-1 p-4 overflow-y-auto space-y-3 bg-white"
        onScroll={(e) => {
          // Determine if user has scrolled up
          const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
          const isAtBottom = Math.abs(scrollHeight - scrollTop - clientHeight) < 10;
          setShouldScrollToBottom(isAtBottom);
        }}
      >
        {/* Load more messages button - only show if we have messages and hasMoreMessages is true */}
        {conversation?.messages?.length > 0 && hasMoreMessages && onLoadMoreMessages && (
          <div className="flex justify-center mb-4">
            <button
              onClick={() => {
                // Set the flag to indicate messages are being loaded via button
                setLoadedViaButton(true);
                onLoadMoreMessages(conversation.conversation_id);
              }}
              className="bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded flex items-center"
              disabled={isLoadingMore}
            >
              {isLoadingMore ? (
                <>
                  <div className="w-4 h-4 border-2 border-gray-600 border-t-transparent rounded-full animate-spin mr-2"></div>
                  Loading...
                </>
              ) : (
                <>
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                  </svg>
                  Load more messages
                </>
              )}
            </button>
          </div>
        )}

        {conversation?.messages?.map((message) => {
          // Special case for appointment messages
          if (message.is_appointment) {
            return (
              <div key={message.id} className="flex justify-center">
                <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg max-w-[85%] shadow">
                  <div className="flex items-center">
                    <span className="mr-2">📅</span>
                    <p className="text-sm text-blue-800 font-medium">{formatMessageContent(message.message_content)}</p>
                  </div>
                  <div className="mt-1 text-xs text-blue-600 text-right">
                    <span className="bg-blue-100 px-2 py-1 rounded capitalize">{message.appointment_status}</span>
                    <span className="ml-2">{new Date(message.timestamp).toLocaleString()}</span>
                    {message.appointment?.has_notified &&
                      <span className="ml-2 bg-green-100 text-green-800 px-2 py-1 rounded">
                        Notified ✓
                      </span>
                    }
                  </div>
                </div>
              </div>
            );
          }

          // Special case for leave message events
          if (message.is_leave_message) {
            return (
              <div key={message.id} className="flex justify-center">
                <div className="bg-orange-50 border border-orange-200 p-3 rounded-lg max-w-[85%] shadow">
                  <div className="flex items-center">
                    <span className="mr-2">💬</span>
                    <p className="text-sm text-orange-800 font-medium">{formatMessageContent(message.message_content)}</p>
                  </div>
                  <div className="mt-1 text-xs text-orange-600 text-right">
                    <span className="bg-orange-100 px-2 py-1 rounded">
                      Leave Message to Clinic
                    </span>
                    <span className="ml-2">{new Date(message.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          }

          // Special case for event messages (notify_clinic_by_human)
          if (message.is_event) {
            return (
              <div key={message.id} className="flex justify-center">
                <div className="bg-green-50 border border-green-200 p-3 rounded-lg max-w-[85%] shadow">
                  <div className="flex items-center">
                    <span className="mr-2">📢</span>
                    <p className="text-sm text-green-800 font-medium">{formatMessageContent(message.message_content)}</p>
                  </div>
                  <div className="mt-1 text-xs text-green-600 text-right">
                    <span className="bg-green-100 px-2 py-1 rounded">
                      {
                        message.event_type === "notify_clinic_by_human" ? "Notified Clinic by Human" : "Notified Clinic"
                      }
                    </span>
                    <span className="ml-2">{new Date(message.timestamp).toLocaleString()}</span>
                  </div>
                </div>
              </div>
            );
          }

          // Regular message handling
          const messageDirection = getMessageDirection(message);
          const isAnnotated = annotatedMessageIds.has(message.id);

          // Find annotations for this message
          const messageAnnotations = annotations.filter(ann =>
            ann.selected_message_ids.includes(message.id)
          );

          return (
            <div
              key={message.id}
              className={`flex ${messageDirection === 'outbound' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`p-3 rounded-lg max-w-[85%] shadow relative ${messageDirection === 'outbound'
                    ? 'bg-[#dcf8c6]'
                    : 'bg-white border border-gray-200'
                  }`}
              >
                {/* Range selection indicators */}
                {isBatchAnnotationMode && (
                  <div className="absolute -top-2 -left-2 z-10">
                    <div
                      className={`w-4 h-4 rounded-full border-2 border-white shadow-sm cursor-pointer transition-all duration-200 ${rangeStart === message.id
                          ? 'bg-orange-500 scale-125' // Start of range
                          : rangeEnd === message.id
                            ? 'bg-orange-600 scale-125' // End of range
                            : selectedMessageIds.has(message.id)
                              ? 'bg-orange-400' // In range
                              : 'bg-gray-400 hover:bg-gray-500' // Not selected
                        }`}
                      onClick={() => handleRangeSelection(message.id)}
                      title={
                        rangeStart === message.id
                          ? 'Range start - click to clear or select end'
                          : rangeEnd === message.id
                            ? 'Range end'
                            : selectedMessageIds.has(message.id)
                              ? 'In selected range'
                              : 'Click to start range selection'
                      }
                    />
                  </div>
                )}

                <AnnotatableMessage
                  message={message}
                  isAnnotated={isAnnotated}
                  messageAnnotations={messageAnnotations}
                  onAnnotationCreated={handleAnnotationCreated}
                  onAnnotationDeleted={handleAnnotationDeleted}
                />
                <div className="mt-1 text-xs text-gray-500 text-right">
                  {new Date(message.timestamp).toLocaleString()}
                </div>
              </div>
            </div>
          );
        })}

        {/* Range selection info */}
        {isBatchAnnotationMode && selectedMessageIds.size > 0 && (
          <div className="bg-gradient-to-r from-orange-50 to-yellow-50 border border-orange-200 p-3 mt-4 rounded-lg shadow-sm">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                <span className="text-sm font-medium text-orange-800">
                  Range selected: {selectedMessageIds.size} messages
                </span>
              </div>
              <div className="flex items-center space-x-2">
                <button
                  onClick={clearRangeSelection}
                  className="text-orange-600 hover:text-orange-800 text-sm hover:bg-orange-100 px-2 py-1 rounded transition-colors"
                  title="Clear selection"
                >
                  Clear Selection
                </button>
                <button
                  onClick={toggleBatchAnnotationMode}
                  className="text-orange-800 hover:text-orange-900 text-sm hover:bg-orange-100 rounded-full w-5 h-5 flex items-center justify-center transition-colors"
                >
                  ✕
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Refresh button after messages - without changing scroll position */}
        {showRefreshButton && (
          <div className="bg-blue-50 p-2 mt-4 rounded flex justify-center">
            <button
              onClick={onRefresh}
              className="text-blue-600 text-sm font-medium hover:text-blue-800 flex items-center"
              disabled={isLoading}
            >
              <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"></path>
              </svg>
              Refresh - last update: {lastRefreshTime}
            </button>
          </div>
        )}
      </div>

      {/* Templates dialog */}
      {isTemplateDialogOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-semibold text-gray-800">Available Templates</h3>
              <button
                onClick={() => setIsTemplateDialogOpen(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {isLoadingTemplates ? (
              <div className="py-8 flex justify-center">
                <div className="w-8 h-8 border-4 border-[#128c7e] border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : (
              <div className="max-h-96 overflow-y-auto">
                {templates.length === 0 ? (
                  <p className="text-gray-500 text-center py-4">No templates available</p>
                ) : (
                  <ul className="space-y-2">
                    {templates.map((template) => (
                      <li key={template.id} className="border rounded-lg p-3 hover:bg-gray-50">
                        <div className="flex justify-between items-start">
                          <div>
                            <h4 className="font-medium text-gray-800">{template.id}</h4>
                            <p className="text-sm text-gray-500 mt-1">{template.content}</p>
                          </div>
                          <button
                            onClick={() => sendTemplate(template.id)}
                            className="ml-2 bg-[#128c7e] text-white px-3 py-1 rounded text-sm hover:bg-[#075e54]"
                          >
                            Select
                          </button>
                        </div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Floating Annotation Form */}
      <FloatingAnnotationForm
        isVisible={isFloatingFormVisible}
        selectedMessageCount={selectedMessageIds.size}
        onSubmit={handleBatchAnnotation}
        onCancel={() => {
          setIsFloatingFormVisible(false);
          clearRangeSelection();
        }}
      />

      {/* Annotations Panel */}
      {isAnnotationsPanelOpen && (
        <AnnotationsPanel
          conversationId={conversation?.conversation_id}
          onClose={() => setIsAnnotationsPanelOpen(false)}
          onAnnotationDeleted={handleAnnotationDeleted}
          onAddToTestPlan={onRequestAddToTestPlan
            ? (annotation, defaultDestination = 'append') => onRequestAddToTestPlan({
              scope: TEST_PLAN_SCOPE.ANNOTATION,
              annotation,
              defaultDestination,
            })
            : undefined}
        />
      )}

      {/* WhatsApp-like input area with Notify Clinic button and Send Template button */}
      <div className="bg-[#f0f0f0] p-2 flex items-center">
        <button
          onClick={() => onSendToClinic(conversation.conversation_id)}
          className="mr-2 bg-[#128c7e] bg-opacity-80 text-white px-2 py-1 rounded text-sm hover:bg-opacity-100"
        >
          Notify Clinic
        </button>
        <button
          onClick={openTemplateDialog}
          className="mr-2 bg-[#0d6efd] bg-opacity-80 text-white px-2 py-1 rounded text-sm hover:bg-opacity-100"
        >
          Send Template
        </button>
        <div className="flex-1 bg-white rounded-full px-4 py-2 flex items-center">
          <input
            type="text"
            className="flex-1 outline-none"
            placeholder="Type a message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyPress={handleKeyPress}
          />
          <button
            onClick={sendMessage}
            className="ml-2 text-[#128c7e] hover:text-[#075e54]"
            disabled={!message.trim()}
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M10.293 3.293a1 1 0 011.414 0l6 6a1 1 0 010 1.414l-6 6a1 1 0 01-1.414-1.414L14.586 11H3a1 1 0 110-2h11.586l-4.293-4.293a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConversationDetail;
